import React from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  ListGroup,
  ListGroupItem,
  Input
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import RealEstateDashboard from "../../realEstate";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import { SUITE_MAP_PLACEHOLDER_MIN_220 } from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function ListingsMapGridPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.realEstate.listListings(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.re.listings" />}>
      <Row className="mb-4">
        <Col md="5">
          <div
            className="bg-light rounded d-flex align-items-center justify-content-center text-muted"
            style={SUITE_MAP_PLACEHOLDER_MIN_220}
          >
            Map cluster (Leaflet / Mapbox)
          </div>
        </Col>
        <Col md="7">
          <AsyncStateBoundary loading={loading} error={error}>
            <Row>
              {(data || []).map(l => (
                <Col xs="12" key={l.id} className="mb-3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="d-flex justify-content-between flex-wrap">
                      <div>
                        <h6 className="mb-1">{l.address}</h6>
                        <div className="small text-muted">{l.dom} DOM</div>
                        {l.views30d != null && (
                          <div className="small text-muted">
                            {l.views30d} views · {l.leads30d ?? 0} leads (30d)
                          </div>
                        )}
                      </div>
                      <div className="text-end">
                        <div className="h5 mb-0">${(l.price / 1000).toFixed(0)}k</div>
                        <Badge>{l.status}</Badge>
                        <Button
                          tag={Link}
                          color="link"
                          size="sm"
                          className="d-block ms-auto"
                          to={l.id}
                          relative="path"
                        >
                          View
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </AsyncStateBoundary>
        </Col>
      </Row>
    </SuitePageShell>
  );
}

function ListingDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.realEstate.getListing(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.re.listing-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h3>{data.address}</h3>
              <p>${data.price}</p>
              <p>DOM {data.dom}</p>
              {data.views30d != null && (
                <p className="small text-muted mb-0">
                  30-day performance: {data.views30d} views, {data.leads30d} leads
                </p>
              )}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function LeadsInboxPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.realEstate.listLeads(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.re.leads" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(l => (
          <Card key={l.id} className="mb-2 border-0 shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-start">
              <div>
                <strong>{l.name}</strong>
                <div className="small text-muted">{l.source}</div>
                <div>Interest: {l.interest}</div>
              </div>
              <Badge color="info">{l.stage}</Badge>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function TransactionDealRoomPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.realEstate.listTransactions(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.re.transactions" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(t => (
          <Card key={t.id} className="mb-4 border-0 shadow-sm">
            <CardBody>
              <h5>{t.property}</h5>
              <p className="text-muted small mb-3">
                Buyer {t.buyer} · Close {t.closeDate}
              </p>
              <ListGroup>
                {(t.checklist || []).map((item, i) => (
                  <ListGroupItem
                    key={i}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <label className="mb-0 d-flex align-items-center gap-2">
                      <Input type="checkbox" readOnly checked={item.done} />{" "}
                      {item.label}
                    </label>
                    <Badge color={item.done ? "success" : "warning"}>
                      {item.done ? "Done" : "Open"}
                    </Badge>
                  </ListGroupItem>
                ))}
                {(!t.checklist || t.checklist.length === 0) && (
                  <ListGroupItem className="text-muted small">
                    Wire transaction milestones from your MLS / transaction
                    coordinator API.
                  </ListGroupItem>
                )}
              </ListGroup>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function RealEstateSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="real-estate" />}>
        <Route index element={<RealEstateDashboard />} />
        <Route path="listings" element={<ListingsMapGridPage />} />
        <Route path="listings/:id" element={<ListingDetailPage />} />
        <Route path="leads" element={<LeadsInboxPage />} />
        <Route path="transactions" element={<TransactionDealRoomPage />} />
      </Route>
    </Routes>
  );
}
